import type {
  CommitStrokeRequest,
  CommitStrokeResponse,
  CommitStrokesBatchRequest,
  CommitStrokesBatchResponse,
  GetStateResponse,
  ClearCanvasResponse,
  StrokeInput,
} from '@twitch-draw/shared';

const EBS_URL = import.meta.env.VITE_EBS_URL || 'http://localhost:3000';
const REQUEST_TIMEOUT = 10000;

class EBSClient {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${EBS_URL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw err;
    }
  }

  async getState(): Promise<GetStateResponse> {
    return this.request<GetStateResponse>('/state');
  }

  async commitStroke(
    stroke: StrokeInput,
    transactionId: string,
    transactionReceipt: string
  ): Promise<CommitStrokeResponse> {
    const body: CommitStrokeRequest = {
      stroke,
      transactionId,
      transactionReceipt,
    };

    return this.request<CommitStrokeResponse>('/stroke', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async commitStrokesBatch(
    strokes: StrokeInput[],
    transactionId: string,
    transactionReceipt: string,
    displayName?: string
  ): Promise<CommitStrokesBatchResponse> {
    const body: CommitStrokesBatchRequest = {
      strokes,
      transactionId,
      transactionReceipt,
      displayName,
    };

    return this.request<CommitStrokesBatchResponse>('/strokes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async clearCanvas(): Promise<ClearCanvasResponse> {
    return this.request<ClearCanvasResponse>('/clear', {
      method: 'POST',
    });
  }
}

export const ebsClient = new EBSClient();
